// src/hooks/useCaptureActivity.js
import {useCallback, useState} from 'react';
import axios from 'axios';
import {useSelector} from "react-redux";

/**
 * Hook: useCaptureActivity
 *
 * Returns a function `captureActivity` with the signature:
 *   captureActivity(email: string, activity: string): Promise<void>
 *
 * Exposes loading / error state if you need to show any feedback.
 */
export function useCaptureActivity() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const baseURL = process.env.REACT_APP_BASEURL;
    const ipAddress = useSelector((state) => state.user.ipAddress);
    /**
     * captureActivity(email, activity)
     *
     * @param {string} email
     * @param {string} activity
     * @returns {Promise<void>}
     */
    const captureActivity = useCallback(
        async (email, activity) => {
            setIsLoading(true);
            setError(null);

            const obj = {
                email: email,
                activity: activity,
                ipAddress: ipAddress
            }

            try {
                await axios.post(
                    `${baseURL}/api/user-usage-tracker/captureActivity`,
                    // {email, activity},
                    obj,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );
                // no response body expected; we treat any 2xx as success
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Unknown error');
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const captureVisit = useCallback(
        async (userVisitTracker) => {
            setIsLoading(true);
            setError(null);

            try {
                await axios.post(
                    `${baseURL}/api/user-visits-tracker/saveUserVisit`,
                    userVisitTracker,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );
                // no response body expected; we treat any 2xx as success
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Unknown error');
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    return {captureActivity, captureVisit, isLoading, error};
}
